import api from "@/api";
import store from "../store";
import auth0 from "auth0-js";
import { appcuesIdentify } from "./appcues";
import { v4 as uuid } from "uuid";

// Typically `state` is just a unique code per request
// however, the API service relies on the redirect url
// to be sent from the console. To avoid updating config
// to store redirect url in the API service, just appending
// a uuid in the JSON that's already beeing sent. This
// will serve as the unique string.
function getStateJson() {
  const state = {
    redirectUri: `${api.props.config.siteBase}${api.props.config.auth0.redirectAfter}`,
    _s: uuid(),
  };

  return JSON.stringify(state);
}

const webAuth = new auth0.WebAuth({
  domain: api.props.config.auth0.clientDomain,
  clientID: api.props.config.auth0.clientId,
  redirectUri: api.props.config.serviceBase + api.props.config.auth0.redirectTo,
  responseType: "code",
  state: getStateJson(),
  audience: api.props.config.auth0.audience,
  scope: "openid email profile user_metadata app_metadata offline_access",
});

const API_KEY = "apiKey";
const USER_ID = "userId";
const ORGANIZATION_ID = "organizationId";
const SUBSCRIPTION_ID = "subscriptionId";
const SUBSCRIPTION_STATUS = "subscriptionStatus";
const CONNECTION_CREATED = "connectionCreated";
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const ORGANIZATION_ID_CLAIM =
  "http://schemas.xmlsoap.org/claims/OrganizationId";

import swal from "sweetalert2";

export function logout() {
  clearApiKey();
  clearSubscriptionId();
  clearSubscriptionStatus();
  clearOrganizationId();
  clearUserId();

  localStorage.removeItem("apc_local_id");
  localStorage.removeItem("apc_user_id");
  localStorage.removeItem("apc_ann_errors");
  localStorage.removeItem("loginTime");

  webAuth.logout({
    returnTo: api.props.config.siteBase,
    clientId: api.props.config.auth0.clientId,
  });
}

export function requireAuth(to, from, next) {
  if (!isLoggedIn()) {
    webAuth.authorize({ path: "/" });
  } else {
    next();
  }
}

function parseCallbackUrl() {
  return new Promise((resolve, reject) => {
    const queryStateParam = getQueryByName("state");
    var parseHashOptions = { hash: window.location.search.replace(/^\?/, "") };
    if (!JSON.parse(queryStateParam)._s) {
      parseHashOptions.state = queryStateParam;
    }
    webAuth.parseHash(parseHashOptions, (err, authResult) => {
      if (err) {
        reject(err);
      } else if (!authResult) {
        reject("Could not parse callback url");
      }

      resolve(authResult);
    });
  });
}

function getUserInfo(authResult) {
  return new Promise((resolve, reject) => {
    webAuth.client.userInfo(authResult.accessToken, (err, userInfo) => {
      if (err) {
        reject(err);
      }
      resolve({ authResult, userInfo });
    });
  });
}

export function handleUserDetails() {
  return new Promise((resolve, reject) => {
    parseCallbackUrl()
      .then((authResult) => getUserInfo(authResult))
      .then((authDetail) => {
        const authResult = authDetail.authResult;
        const userInfo = authDetail.userInfo;
        const userId = userInfo.sub;
        const organizationId = userInfo[ORGANIZATION_ID_CLAIM];

        setAccessToken(authResult.accessToken);
        setRefreshToken(authResult.refreshToken);
        setUserId(userId);
        setOrganizationId(organizationId);
        setSubscriptionId();

        let url = api.props.config.siteBase + "/#/dashboard";
        if (!getSubscriptionId()) {
          //if we have no subscription lets check to see if the redirect wants us to start up a checkout
          const state = JSON.parse(getQueryByName("state"));
          if (state.redirectUri !== undefined) {
            //lets start a checkout!
            const split = state.redirectUri.split("?");
            if (split.length == 2) {
              const redirectQuery = new URLSearchParams(split[1]);
              if (redirectQuery.has("planId")) {
                url =
                  api.props.config.siteBase +
                  "/?hosted_type=new&" +
                  split[1] +
                  "#/dashboard";
              }
            }
          }
          resolve(url);
        } else {
          store
            .dispatch(
              "subscription/loadSubscription",
              localStorage.subscriptionId
            )
            .then(result => {
              setSubscriptionStatus(result.status);
              if (window.Appcues !== undefined) {
                appcuesIdentify(localStorage.userId, {
                  // Unique identifier for current user
                  PlanName: result.platformPlan.name, // Current user’s plan tier
                  PlanType: result.platformPlan.planType,
                  LSC_SubStatus: result.platformPlan.status, // Current user’s plan type
                  LSC_SupAddonName: result.supportPlan != null ? result.supportPlan.name : null,
                  LSC_TrialEndDate: result.trialEndOn,
                  LSC_TrialStartDate: result.trialStartOn,
                  LSC_SubStartDate: result.activatedOn,
                  LSC_SubEndDate: result.currentTermEnd,
                });
              }
              resolve(url);
            });
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
}

function getUserId() {
  return localStorage.getItem(USER_ID);
}

function getOrganizationId() {
  return localStorage.getItem(ORGANIZATION_ID);
}

function getSubscriptionId() {
  return localStorage.getItem(SUBSCRIPTION_ID);
}

function getSubscriptionStatus() {
  return localStorage.getItem(SUBSCRIPTION_STATUS);
}

export function getConnectionCreated() {
  return localStorage.getItem(CONNECTION_CREATED);
}

function clearApiKey() {
  localStorage.removeItem(API_KEY);
}

function clearUserId() {
  localStorage.removeItem(USER_ID);
}

function clearOrganizationId() {
  localStorage.removeItem(ORGANIZATION_ID);
}

function clearSubscriptionId() {
  localStorage.removeItem(SUBSCRIPTION_ID);
}

function clearSubscriptionStatus() {
  localStorage.removeItem(SUBSCRIPTION_STATUS);
}

// Helper function that will allow us to extract the access_token and id_token
function getQueryByName(name) {
  const query = new URLSearchParams(window.location.search);
  return query.get(name);
}

function setAccessToken(accessToken) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
}

function setRefreshToken(refreshToken) {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function setUserId(userId) {
  if (userId !== null) {
    localStorage.setItem(USER_ID, userId);
  }
}

function setOrganizationId(organizationId) {
  if (organizationId !== null) {
    localStorage.setItem(ORGANIZATION_ID, organizationId);
  }
}

export function setSubscriptionId(passedInSubscriptionId) {
  const subscriptionId = getQueryByName(SUBSCRIPTION_ID);
  if (subscriptionId !== null) {
    localStorage.setItem(SUBSCRIPTION_ID, subscriptionId);
  } else if (
    passedInSubscriptionId !== undefined &&
    passedInSubscriptionId !== null
  ) {
    localStorage.setItem(SUBSCRIPTION_ID, passedInSubscriptionId);
  }
}

export function setSubscriptionStatus(status) {
  localStorage.setItem(SUBSCRIPTION_STATUS, status);
}

export function setConnectionCreated(connectionCreated) {
  localStorage.setItem(CONNECTION_CREATED, connectionCreated);
}

export function isLoggedIn() {
  const userId = getUserId();
  const organizationId = getOrganizationId();
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  return !!userId && !!organizationId && !!accessToken;
}

export function checkSubscription(to, router) {
  const subscriptionId = getSubscriptionId();
  const subscriptionStatus = getSubscriptionStatus();
  let query = new URLSearchParams(window.location.search);
  if (query.keys.length === 0) {
    //lets check if the hash has a query string for us
    const split = window.location.hash.split("?");
    if (split.length == 2) {
      query = new URLSearchParams(decodeURIComponent(split[1]));
    }
  }
  if (
    subscriptionId ||
    (query.has("hosted_type") && query.get("hosted_type") === "new")
  ) {
    //if we have a subscription check if it is cancelled
    if (
      subscriptionStatus !== null &&
      subscriptionStatus !== "" &&
      subscriptionStatus === "cancelled" &&
      (!to.path.includes("subscription") && !to.path.includes("billing"))
    ) {
      let html = `<iframe src='https://www.liveswitch.com/p/lsc-console-reactivate-modal/' id="hsIframe" class = "missSubscriptionModal" scrolling='no'></iframe>`;
      swal
        .fire({
          html: html,
          showConfirmButton: false
        })
        .then(result => {
          if (result.value) {
            window.location.replace(
              api.props.config.siteBase + "/?start_plan" + "#/subscription"
            );
          }
        });
    }
    return;
  }

  if (Array.from(query.entries()).length === 0 || subscriptionId === null) {
    let html =
      "<strong>Welcome</strong><br /><br />" +
      "You're just steps away from amazingness!  We just need to grab your payment details and initialize your subscription.<br /><br />" +
      "Don't worry, LiveSwitch Cloud is 100% free during the trial period.<br /><br />" +
      "<a href='/#/logout'>have another account? - Login</a>";

    if (api.props.config.environment !== "prod") {
      html = `<iframe src='https://www.liveswitch.com/p/lsc-console-welcome-modal/' id="hsIframe" class = "welcomeSubscriptionModal" data-environment='${
        api.props.config.environment
      }' scrolling='no'></iframe>`;
    } else {
      html = `<iframe src='https://www.liveswitch.com/p/lsc-console-welcome-modal/' id="hsIframe" class = "welcomeSubscriptionModal" data-environment='${
        api.props.config.environment
      }' scrolling='no'></iframe>`;
    }
    swal
      .fire({
        html: html,
        showConfirmButton: false,
        cancelButtonText: "No",
        buttonsStyling: false,
        allowOutsideClick: false
      })
      .then(result => {
        if (result.value) {
          router.push({
            path: "",
            query: {
              hosted_type: "new",
              planId: "lsc-starter-v1",
              planQuantity: "1",
              addonsId0: "support-plan-free-v1",
              addonsQuantity0: "1"
            }
          });
        }
      });
  }
}

export function getCurrentUser() {
  const users = store.getters["user/loadedEntity"];
  let user = undefined;
  if (users.length > 0) {
    for (let i = 0; i < users.length; i++) {
      if (users[i].id == localStorage.userId) {
        user = users[i];
      }
    }
  }
  return user;
}
